import { z } from 'zod'
import { oc } from '@orpc/contract'

// A diagnosis linked to a service line, resolved from `Claim.diagnosis[]`
// (either a referenced `Condition` or an inline codeable concept). `system` is
// carried so the drawer editor can round-trip the coding losslessly on save.
export const billingDiagnosisSchema = z.object({
  code: z.string().nullable(),
  display: z.string().nullable(),
  system: z.string().nullable(),
})

// One Claim service line: the procedure code + display + system, its modifiers,
// unit price/quantity, and the diagnoses linked to it via
// `Claim.item[].diagnosisSequence`. Every field the drawer editor edits is
// surfaced so save can be a lossless last-write-wins replace (no field the read
// path drops would be silently wiped on save).
export const billingServiceLineSchema = z.object({
  code: z.string().nullable(),
  display: z.string().nullable(),
  system: z.string().nullable(),
  modifiers: z.array(z.string()),
  price: z.number().nullable(),
  quantity: z.number().nullable(),
  diagnoses: z.array(billingDiagnosisSchema),
})

// Billing authorship derived from the Claim's metadata/history — no new stored
// fields: Modified from `meta.lastUpdated`/`meta.author`, Created from the oldest
// `_history` version. The `by` fields are resolved display names (null when the
// author is unknown).
export const billingAuthoringSchema = z.object({
  createdOn: z.string().nullable(),
  createdBy: z.string().nullable(),
  modifiedOn: z.string().nullable(),
  modifiedBy: z.string().nullable(),
})

// Full editable billing detail for one encounter. `status` is the raw Claim
// Status code from the encounter tag (interpreted by the frontend mirror);
// `authoring` is null when the encounter has no Claim yet (the "+ Add codes"
// empty state). `encounterDiagnoses` is the encounter's own diagnoses
// (`Encounter.diagnosis[]`, i.e. the progress note's assessment) — independent of
// any Claim — so the drawer can prepopulate a newly added service line with all of
// them even on an encounter that has never been coded.
export const billingDetailSchema = z.object({
  encounterId: z.string(),
  status: z.string().nullable(),
  // The date this claim was submitted, taken from the preserved billing-status
  // date on the encounter and surfaced only while the status is Submitted (null
  // otherwise). Honors the migration requirement that a record's existing
  // billing-status date carry over as its Submitted On date.
  submittedOn: z.string().nullable(),
  serviceLines: z.array(billingServiceLineSchema),
  encounterDiagnoses: z.array(billingDiagnosisSchema),
  authoring: billingAuthoringSchema.nullable(),
})

export const billingDetail = oc
  .route({ method: 'GET', path: '/v2/billing/detail/{encounterId}' })
  .input(z.object({ encounterId: z.string() }))
  .output(billingDetailSchema)
